import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Gallery = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery`);
      setImages(response.data.images);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Fallback demo images if database is empty
  const demoImages = [
    { image_url: 'https://images.unsplash.com/photo-1663811397133-2d1f5addd9d5', title_en: 'Luxury Bathroom', title_ar: 'حمام فاخر', category: 'residential' },
    { image_url: 'https://images.pexels.com/photos/33685863/pexels-photo-33685863.jpeg', title_en: 'Modern Interior', title_ar: 'تصميم داخلي حديث', category: 'hospitality' },
    { image_url: 'https://images.unsplash.com/photo-1744025098626-66c0b9cb1ba8', title_en: 'Marble Flooring', title_ar: 'أرضية رخامية', category: 'corporate' },
    { image_url: 'https://images.unsplash.com/photo-1600421495550-158936f5ecfa', title_en: 'Commercial Space', title_ar: 'مساحة تجارية', category: 'malls' },
    { image_url: 'https://images.unsplash.com/photo-1690049098415-29f96fca22c0', title_en: 'Stone Texture', title_ar: 'نسيج حجري', category: 'materials' },
    { image_url: 'https://images.unsplash.com/photo-1627382385242-d41b912877eb', title_en: 'Marble Staircase', title_ar: 'سلم رخامي', category: 'residential' },
  ];

  const displayImages = images.length > 0 ? images : demoImages;

  return (
    <div className="pt-20">
      <section className="section-padding bg-[#F5F5F0]" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl text-center mx-auto">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">
              {t('gallery')}
            </p>
            <h1 className={`text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-none mb-6 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`} data-testid="gallery-title">
              {t('galleryTitle')}
            </h1>
            <p className={`text-xl text-gray-700 ${
              isRTL ? 'font-arabic' : ''
            }`}>
              {t('gallerySubtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="text-gray-500">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayImages.map((image, index) => (
                <div
                  key={index}
                  className="group overflow-hidden bg-white hover:shadow-xl transition-all duration-500"
                  data-testid={`gallery-item-${index}`}
                >
                  <div className="overflow-hidden aspect-[4/3]">
                    <img
                      src={image.image_url}
                      alt={isRTL ? image.title_ar : image.title_en}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 border border-t-0 border-black/5 group-hover:border-[#C4A661]">
                    <h3 className={`text-lg font-medium ${
                      isRTL ? 'font-arabic' : 'font-heading'
                    }`}>
                      {isRTL ? image.title_ar : image.title_en}
                    </h3>
                    {image.description_en && (
                      <p className={`text-sm text-gray-600 mt-2 ${
                        isRTL ? 'font-arabic' : ''
                      }`}>
                        {isRTL ? image.description_ar : image.description_en}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Gallery;
